// Pure, side-effect-free derivations for the Staff Management record tabs
// (attendance / leave / roster / advances / deductions).
//
// These are lifted VERBATIM out of StaffManagement.js so the shell can stop
// owning the derivation logic and each tab can eventually live in its own file.
// They are pure functions of their inputs — every value the original useMemo
// closed over is passed in explicitly, and component-local helper closures
// (isStaffEligibleOnDate, getSalarySummaryForCalendarDate) are passed as callbacks
// so behaviour is byte-identical to the inline version.
//
// While the extraction is being validated, the shell runs BOTH the inline
// version and these functions and deep-compares the output (see the safety net
// in StaffManagement.js). Once verified live, the inline copies are removed.

import { offDayFilled } from '../components/LifecycleBadge';

const CALENDAR_TABS = ['attendance', 'leave', 'roster', 'advances', 'deductions'];

export const LIFECYCLE_MARKER_TYPES = ['employed', 'reemployed', 'terminated'];

// Punch notes are persisted as "<Reason> - <free text>". The Reason half is an
// enumerated value; anything else is plain notes. Single source of truth for the
// dropdown options, the table's Reason column, and the edit-modal split.
export const ATTENDANCE_REASONS = [
  'Start Shift', 'End Shift', 'Washroom', 'Meal', 'Official', 'Prayers',
  'Supermarket', 'Water Fetch', 'Banking', 'Waste Disposal', 'Delivery Handling',
  'Washing', 'Personal', 'Other',
];

/**
 * Split a stored punch `notes` string into its enumerated Reason and the free-text
 * remainder. The first segment counts as a Reason ONLY when it matches the enum —
 * so a note that merely happens to contain " - " is not mangled into a fake reason.
 */
export function splitPunchNotes(notes) {
  const parts = String(notes || '').split(' - ');
  const head = (parts[0] || '').trim();
  if (ATTENDANCE_REASONS.includes(head)) {
    return { reason: head, notes: parts.slice(1).join(' - ').trim() };
  }
  return { reason: '', notes: String(notes || '').trim() };
}

/**
 * The filtered/sorted row source for the active tab, before missing-entry placeholders.
 * Mirrors the inline `currentItems` useMemo.
 */
export function computeCurrentItems({
  activeTab,
  staff,
  attendance,
  leaveEntries,
  offDayRecords,
  lifecycleEventsList,
  rosters,
  advances,
  shopHours,
  shopHoursListTab,
  publicHolidays,
  search,
  startDate,
  endDate,
  filterEndCalendarDate,
  startTime,
  endTime,
  staffFilter,
  smSortKey,
  smSortData,
  businessDayStartHour,
  getBusinessDateForDateTimeValue,
  matchesAttRecordFilter,
}) {
  if (activeTab === 'staff') return smSortData(staff);
  if (activeTab === 'shop_hours') return shopHoursListTab === 'holidays' ? publicHolidays : shopHours;
  let source;
  if (activeTab === 'attendance') {
    const q = search.toLowerCase();
    let punches = attendance.map(a => ({ ...a, _entryType: 'punch', _sortDate: a.punch_time ? getBusinessDateForDateTimeValue(a.punch_time, businessDayStartHour) : '' }));
    if (search) punches = punches.filter(a => Object.values(a).some(v => String(v ?? '').toLowerCase().includes(q)));
    // Filter by full datetime for business day filtering
    punches = punches.filter(a => {
      if (!a.punch_time) return false;
      const punchDate = new Date(a.punch_time);
      const startDateTime = new Date(startDate + 'T' + startTime + ':00');
      const endDateTime = new Date(filterEndCalendarDate + 'T' + endTime + ':00');
      return punchDate >= startDateTime && punchDate <= endDateTime;
    });
    if (staffFilter) punches = punches.filter(a => String(a.staff) === String(staffFilter));
    // Off-day (Leave/Holiday/Absentee) records surface as their own rows so the day off can be
    // removed independently of any In/Out punches on the same date.
    const staffNameById = {};
    staff.forEach(s => { staffNameById[String(s.id)] = s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim(); });
    let offRows = offDayRecords.map(r => ({
      ...r, _entryType: 'offday', _offDay: true, _sortDate: String(r.date).substring(0, 10),
      staff_name: r.staff_name || staffNameById[String(r.staff)] || '',
    }));
    offRows = offRows.filter(r => r._sortDate >= startDate && r._sortDate <= endDate);
    if (staffFilter) offRows = offRows.filter(r => String(r.staff) === String(staffFilter));
    if (search) offRows = offRows.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(q)));
    // Legacy leave records (StaffLeave) also surface as their own per-day rows, so a leave that
    // coexists with In/Out punches is visible and manageable in the table (not just the calendar).
    const offDayKeys = new Set(offRows.map(r => `${r.staff}_${r._sortDate}`));
    let legacyLeaveRows = [];
    leaveEntries.forEach(l => {
      if (!l.start_date) return;
      const from = l.start_date > startDate ? l.start_date : startDate;
      const to = (l.end_date || l.start_date) < endDate ? (l.end_date || l.start_date) : endDate;
      for (let d = new Date(from + 'T12:00:00'); d <= new Date(to + 'T12:00:00'); d.setDate(d.getDate() + 1)) {
        const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (offDayKeys.has(`${l.staff}_${ds}`)) continue; // already shown as a unified off-day row
        legacyLeaveRows.push({
          id: l.id, staff: l.staff, _entryType: 'leave', _onLeave: true, _leaveData: l, _date: ds, _sortDate: ds,
          staff_name: l.staff_name || staffNameById[String(l.staff)] || '',
        });
      }
    });
    if (staffFilter) legacyLeaveRows = legacyLeaveRows.filter(r => String(r.staff) === String(staffFilter));
    if (search) legacyLeaveRows = legacyLeaveRows.filter(r => Object.values(r._leaveData || {}).some(v => String(v ?? '').toLowerCase().includes(q)));
    // Employment / re-employment / termination markers as their own rows.
    let lifecycleRows = (lifecycleEventsList || [])
      .filter(e => LIFECYCLE_MARKER_TYPES.includes(e.event_type))
      .map(e => ({ id: e.id, staff: e.staff, _entryType: 'lifecycle', _lifecycle: true, _lifeEvent: e, _date: String(e.event_date || '').substring(0, 10), _sortDate: String(e.event_date || '').substring(0, 10), staff_name: e.staff_name || staffNameById[String(e.staff)] || '' }))
      .filter(r => r._sortDate >= startDate && r._sortDate <= endDate);
    if (staffFilter) lifecycleRows = lifecycleRows.filter(r => String(r.staff) === String(staffFilter));
    if (search) lifecycleRows = lifecycleRows.filter(r => `${r.staff_name} ${r._lifeEvent.event_type_label || r._lifeEvent.event_type}`.toLowerCase().includes(q));
    let combined = [...punches, ...offRows, ...legacyLeaveRows, ...lifecycleRows];
    combined = combined.filter(r => matchesAttRecordFilter(r));
    return combined.sort((a, b) => String(b._sortDate || '').localeCompare(String(a._sortDate || '')));
  }
  if (activeTab === 'leave') {
    const q = search.toLowerCase();
    let leaves = leaveEntries.map(l => ({ ...l, _entryType: 'leave', _sortDate: l.start_date || '' }));
    if (search) leaves = leaves.filter(l => Object.values(l).some(v => String(v ?? '').toLowerCase().includes(q)));
    leaves = leaves.filter(l => l.start_date <= endDate && l.end_date >= startDate);
    if (staffFilter) leaves = leaves.filter(l => String(l.staff) === String(staffFilter));
    return [...leaves].sort((a, b) => b._sortDate.localeCompare(a._sortDate));
  }
  source = activeTab === 'roster' ? rosters : activeTab === 'deductions' ? advances.filter(item => item.payment_type === 'deduction') : advances;
  if (search) {
    const q = search.toLowerCase();
    source = source.filter((item) => Object.values(item).some((value) => String(value ?? '').toLowerCase().includes(q)));
  }
  const dateKey = activeTab === 'roster' ? 'roster_date' : 'advance_date';
  source = source.filter((item) => {
    const val = item[dateKey];
    if (!val) return true;
    const d = String(val).substring(0, 10);
    return d >= startDate && d <= endDate;
  });
  if (staffFilter) source = source.filter((item) => String(item.staff) === String(staffFilter));
  if (!smSortKey) return [...source].sort((a, b) => String(b[dateKey] || '').localeCompare(String(a[dateKey] || '')));
  return smSortData(source);
}

/**
 * Attendance rows with missing-entry placeholders interleaved at the exact point the
 * in -> out -> in -> out alternation breaks. Mirrors the inline `displayItems` useMemo.
 */
export function computeDisplayItems({
  activeTab,
  showAllStaff,
  currentItems,
  staff,
  staffFilter,
  startDate,
  endDate,
  leaveMap,
  offDayMap,
  lifecycleMap,
  businessDayStartHour,
  getBusinessDateForDateTimeValue,
  isStaffEligibleOnDate,
  matchesAttRecordFilter,
}) {
  if (activeTab !== 'attendance' || !showAllStaff) return currentItems;
  const scopeStaff = staffFilter
    ? staff.filter(s => String(s.id) === String(staffFilter))
    : staff;
  // Cover every business date in the selected range (for all staff in scope), so a missing entry
  // shows even on a day nobody punched — and format the date in LOCAL time (toISOString() would
  // shift it back a day in timezones ahead of UTC, e.g. PKT turning 12 Jul into 11 Jul).
  const datesToCover = [];
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    datesToCover.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  datesToCover.reverse();
  if (datesToCover.length === 0) return currentItems;
  const mkPh = (s, date, punch_type, extra = {}) => ({
    id: null, staff: s.id,
    staff_name: s.full_name || ((s.first_name || '') + ' ' + (s.last_name || '')).trim(),
    punch_time: null, punch_type, notes: '', _placeholder: true, _date: date, _sortDate: date, ...extra,
  });
  const result = [];
  for (const date of datesToCover) {
    const dayRecs = currentItems.filter(r => r.punch_time && ((r._sortDate || getBusinessDateForDateTimeValue(r.punch_time, businessDayStartHour)) === date));
    for (const s of scopeStaff) {
      const staffRecs = dayRecs.filter(r => String(r.staff) === String(s.id));
      if (!isStaffEligibleOnDate(s, date)) {
        staffRecs.forEach(r => result.push(r));
        continue;
      }
      const od = offDayMap[`${s.id}_${date}`];
      // A migrated leave has BOTH a legacy StaffLeave and a performance off-day; show only the
      // unified off-day and suppress the old legacy-leave row to avoid a duplicate.
      const lv = od ? null : leaveMap[`${s.id}_${date}`];
      // Unified off-day (Leave/Holiday/Absentee) record — always shown as its own row.
      if (od) result.push({ ...od, id: od.id, staff: s.id, _entryType: 'offday', _offDay: true, _date: date, _sortDate: date, staff_name: s.full_name || ((s.first_name || '') + ' ' + (s.last_name || '')).trim() });
      // Legacy leave — always shown when present (even alongside punches), so it can be managed.
      if (lv) result.push(mkPh(s, date, 'in', { _onLeave: true, _leaveData: lv }));
      // Employment / re-employment / termination markers.
      const life = lifecycleMap[`${s.id}_${date}`];
      if (life && life.length) life.forEach(ev => result.push({ id: ev.id, staff: s.id, _entryType: 'lifecycle', _lifecycle: true, _lifeEvent: ev, _date: date, _sortDate: date, staff_name: s.full_name || ((s.first_name || '') + ' ' + (s.last_name || '')).trim() }));
      // Walk the day's punches in time order and INTERLEAVE a missing-entry placeholder exactly
      // where the in → out → in → out alternation breaks (an OUT where an IN was due, or vice
      // versa), so the indicator sits at the right spot in the sequence. Placeholders carry a
      // punch_time ~1s off the neighbouring punch so they stay put when sorted by time.
      const sorted = [...staffRecs].sort((a, b) => new Date(a.punch_time) - new Date(b.punch_time));
      if (!lv && !od) {
        if (sorted.length === 0) {
          result.push(mkPh(s, date, 'in', { _placeholderType: 'missing_in' }));
        } else {
          let expecting = 'in';
          for (const p of sorted) {
            if (p.punch_type !== expecting) {
              const ph = mkPh(s, date, expecting, { _placeholderType: expecting === 'in' ? 'missing_in' : 'missing_out' });
              ph.punch_time = new Date(new Date(p.punch_time).getTime() - 1000).toISOString();
              result.push(ph);
            }
            result.push(p);
            expecting = p.punch_type === 'in' ? 'out' : 'in';
          }
          // Ended mid-pair (last punch was an IN) → its OUT is still missing.
          if (expecting === 'out') {
            const last = sorted[sorted.length - 1];
            // Only a genuine enum Reason pre-fills the fill-placeholder dropdown; arbitrary
            // note text must not be injected into an enumerated field.
            const lastReason = splitPunchNotes(last?.notes).reason;
            const ph = mkPh(s, date, 'out', { _placeholderType: 'missing_out', _pendingReason: lastReason });
            ph.punch_time = new Date(new Date(last.punch_time).getTime() + 1000).toISOString();
            result.push(ph);
          }
        }
      } else {
        sorted.forEach(r => result.push(r));
      }
    }
  }
  return result.filter(r => matchesAttRecordFilter(r));
}

/**
 * Date-grouped, per-group-sorted rows for the record tabs.
 * Mirrors the inline `groupedByDate` useMemo. Returns null when not applicable.
 */
export function computeGroupedByDate({
  activeTab,
  displayItems,
  businessDayStartHour,
  getBusinessDateForDateTimeValue,
  smSortData,
  attnSortAccessor,
}) {
  if (!CALENDAR_TABS.includes(activeTab)) return null;
  const groups = {};
  displayItems.forEach(item => {
    let date = '';
    if (activeTab === 'attendance') date = item._sortDate || (item.punch_time ? getBusinessDateForDateTimeValue(item.punch_time, businessDayStartHour) : (item._date || ''));
    else if (activeTab === 'leave') date = item.start_date || '';
    else if (activeTab === 'roster') date = item.roster_date || '';
    else if (activeTab === 'advances' || activeTab === 'deductions') date = item.advance_date || '';
    if (!groups[date]) groups[date] = [];
    groups[date].push(item);
  });
  // Sort items within each date group
  Object.keys(groups).forEach(date => {
    groups[date] = smSortData(groups[date], activeTab === 'attendance' ? attnSortAccessor : undefined);
  });
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

/**
 * Month grid: per-day counts/markers for the active record tab.
 * Mirrors the inline `calendarData` useMemo.
 */
export function computeCalendarData({
  activeTab,
  calMonthYM,
  attendance,
  leaveEntries,
  rosters,
  advances,
  leaveMap,
  lifecycleMap,
  offDayMap,
  staff,
  staffFilter,
  businessDayStartHour,
  getBusinessDateForDateTimeValue,
  isStaffEligibleOnDate,
  getSalarySummaryForCalendarDate,
}) {
  if (!CALENDAR_TABS.includes(activeTab)) return {};
  const [yr, mo] = calMonthYM.split('-').map(Number);
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const scopeStaff = staffFilter
    ? staff.filter(s => String(s.id) === String(staffFilter))
    : staff;
  const result = {};
  for (let day = 1; day <= daysInMonth; day++) {
    const ds = `${yr}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const eligible = scopeStaff.filter(s => isStaffEligibleOnDate(s, ds));
    if (activeTab === 'attendance') {
      const dayAtt = attendance.filter(a => a.punch_time && getBusinessDateForDateTimeValue(a.punch_time, businessDayStartHour) === ds);
      const filteredAtt = staffFilter ? dayAtt.filter(a => String(a.staff) === String(staffFilter)) : dayAtt;
      let present = 0, onLeave = 0, missing = 0;
      const onLeaveNames = [];
      for (const s of eligible) {
        const od = offDayMap[`${s.id}_${ds}`];
        const lv = od ? null : leaveMap[`${s.id}_${ds}`];
        const staffPunches = filteredAtt.filter(a => String(a.staff) === String(s.id));
        let hasMissing = false;
        const sorted = [...staffPunches].sort((a, b) => new Date(a.punch_time) - new Date(b.punch_time));
        if (sorted.length === 0) {
          hasMissing = true;
        } else {
          let expecting = 'in';
          for (const p of sorted) {
            if (p.punch_type !== expecting) { hasMissing = true; break; }
            expecting = p.punch_type === 'in' ? 'out' : 'in';
          }
          if (expecting === 'out') hasMissing = true; // ended on an unmatched IN
        }
        if (od) { /* off-day: shown via the dayOffs icon; not present/missing/legacy-leave */ }
        else if (lv) { onLeave++; onLeaveNames.push(s.full_name || `${s.first_name} ${s.last_name}`.trim()); }
        else if (hasMissing) missing++;
        else present++;
      }
      const dayEvents = Object.keys(lifecycleMap)
        .filter(k => k.endsWith(`_${ds}`) && (!staffFilter || k.startsWith(`${staffFilter}_`)))
        .flatMap(k => lifecycleMap[k])
        .map(ev => ({ type: ev.event_type, name: (ev.staff_name || '').split(' ')[0] }));
      const dayOffs = Object.keys(offDayMap)
        .filter(k => k.endsWith(`_${ds}`) && (!staffFilter || k.startsWith(`${staffFilter}_`)))
        .map(k => offDayMap[k])
        .map(r => ({ type: r.violation_type, name: (r.staff_name || '').split(' ')[0], filled: offDayFilled(r.violation_type, r.is_warning) }));
      result[ds] = { present, onLeave, onLeaveNames, missing, total: eligible.length, events: dayEvents, dayOffs };
    } else if (activeTab === 'leave') {
      const dayLeaves = leaveEntries.filter(l => l.start_date <= ds && (l.end_date || l.start_date) >= ds);
      const filtered = staffFilter ? dayLeaves.filter(l => String(l.staff) === String(staffFilter)) : dayLeaves;
      const staffNames = filtered.map(l => {
        const s = staff.find(st => String(st.id) === String(l.staff));
        return s ? (s.full_name || `${s.first_name} ${s.last_name}`.trim()) : '';
      }).filter(n => n);
      result[ds] = { count: filtered.length, staffNames };
    } else if (activeTab === 'roster') {
      const dayRosters = rosters.filter(r => r.roster_date === ds);
      const filtered = staffFilter ? dayRosters.filter(r => String(r.staff) === String(staffFilter)) : dayRosters;
      const withRoster = new Set(filtered.map(r => String(r.staff)));
      result[ds] = { count: filtered.length, rosterCount: eligible.filter(s => withRoster.has(String(s.id))).length, total: eligible.length };
    } else if (activeTab === 'advances' || activeTab === 'deductions') {
      const dayAdv = advances.filter(a => a.advance_date === ds && (activeTab === 'deductions' ? a.payment_type === 'deduction' : true));
      const filtered = staffFilter ? dayAdv.filter(a => String(a.staff) === String(staffFilter)) : dayAdv;
      const salaryPaid = filtered.filter(a => a.payment_type === 'salary');
      const salaryPaidTotal = salaryPaid.reduce((s, a) => s + parseFloat(a.amount || 0), 0);
      const salaryPaidStaff = salaryPaid.map(a => {
        const s = staff.find(st => String(st.id) === String(a.staff));
        return a.staff_name || (s ? (s.full_name || `${s.first_name} ${s.last_name}`.trim()) : '');
      }).filter(n => n);

      const advancesPaid = filtered.filter(a => ['advance', 'bonus', 'gift', 'deduction'].includes(a.payment_type));
      const advancesTotal = advancesPaid.reduce((s, a) => s + parseFloat(a.amount || 0), 0);
      const advancesStaff = advancesPaid.map(a => {
        const s = staff.find(st => String(st.id) === String(a.staff));
        return a.staff_name || (s ? (s.full_name || `${s.first_name} ${s.last_name}`.trim()) : '');
      }).filter(n => n);

      const salaryDueList = scopeStaff
        .map(st => {
          const dueInfo = getSalarySummaryForCalendarDate(st, ds);
          return {
            ...st,
            _dueDate: dueInfo.dueDate,
            _netPayable: dueInfo.payable,
          };
        })
        .filter(st => st._dueDate === ds && st._netPayable > 0);
      const salaryDueStaff = salaryDueList;

      const salaryDueTotal = salaryDueList.reduce((s, st) => s + st._netPayable, 0);

      const salaryDueStaffNames = salaryDueStaff.map(s => (s.full_name || `${s.first_name} ${s.last_name}`.trim())).filter(n => n);

      result[ds] = {
        count: filtered.length,
        total: filtered.reduce((s, a) => s + parseFloat(a.amount || 0), 0),
        salaryPaidTotal: salaryPaidTotal,
        salaryPaidStaff: salaryPaidStaff,
        advancesTotal: advancesTotal,
        advancesStaff: advancesStaff,
        salaryDueStaff: salaryDueStaff,
        salaryDueTotal: salaryDueTotal,
        salaryDueStaffNames: salaryDueStaffNames,
      };
    }
  }
  return result;
}

/**
 * Year grid: per-month rollups for the active record tab.
 * Mirrors the inline `yearCalData` useMemo. Returns null when not applicable.
 */
export function computeYearCalData({
  datePreset,
  activeTab,
  startDate,
  attendance,
  leaveEntries,
  rosters,
  advances,
  staffFilter,
  businessDayStartHour,
  getBusinessDateForDateTimeValue,
}) {
  if (datePreset !== 'year') return null;
  if (!CALENDAR_TABS.includes(activeTab)) return null;
  const yr = new Date(startDate || new Date()).getFullYear();
  const result = {};
  for (let m = 0; m < 12; m++) {
    const ms = `${yr}-${String(m + 1).padStart(2, '0')}`;
    if (activeTab === 'attendance') {
      const monthAtt = attendance.filter(a => a.punch_time && getBusinessDateForDateTimeValue(a.punch_time, businessDayStartHour).substring(0, 7) === ms && a.punch_type === 'in');
      const filtAtt = staffFilter ? monthAtt.filter(a => String(a.staff) === String(staffFilter)) : monthAtt;
      const monthLeaves = leaveEntries.filter(l => String(l.start_date).substring(0, 7) <= ms && String(l.end_date || l.start_date).substring(0, 7) >= ms);
      const filtLeaves = staffFilter ? monthLeaves.filter(l => String(l.staff) === String(staffFilter)) : monthLeaves;
      result[ms] = { present: new Set(filtAtt.map(a => a.staff + '_' + getBusinessDateForDateTimeValue(a.punch_time, businessDayStartHour))).size, leaves: filtLeaves.length };
    } else if (activeTab === 'leave') {
      const monthLeaves = leaveEntries.filter(l => String(l.start_date).substring(0, 7) <= ms && String(l.end_date || l.start_date).substring(0, 7) >= ms);
      const filtered = staffFilter ? monthLeaves.filter(l => String(l.staff) === String(staffFilter)) : monthLeaves;
      result[ms] = { count: filtered.length };
    } else if (activeTab === 'roster') {
      const monthRosters = rosters.filter(r => String(r.roster_date).substring(0, 7) === ms);
      const filtered = staffFilter ? monthRosters.filter(r => String(r.staff) === String(staffFilter)) : monthRosters;
      result[ms] = { count: filtered.length };
    } else if (activeTab === 'advances' || activeTab === 'deductions') {
      const monthAdv = advances.filter(a => String(a.advance_date).substring(0, 7) === ms && (activeTab === 'deductions' ? a.payment_type === 'deduction' : true));
      const filtered = staffFilter ? monthAdv.filter(a => String(a.staff) === String(staffFilter)) : monthAdv;
      result[ms] = { count: filtered.length, total: filtered.reduce((s, a) => s + parseFloat(a.amount || 0), 0) };
    }
  }
  return result;
}
